import { Command } from "cmdk";
import { Keyboard, LogOut, MoonStar, Search, Sparkles, SunMedium } from "lucide-react";
import { useEffect } from "react";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@bigpp/ui/components/dialog";

import { signOut } from "../sign-out.ts";
import { useTheme } from "../theme.ts";
import type { Starter } from "./TaskPane.tsx";

type Props = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  starters: readonly Starter[];
  onPickPrompt: (prompt: string) => void;
  onShowKeymap: () => void;
  running: boolean;
};

export function CommandPalette({
  open,
  onOpenChange,
  starters,
  onPickPrompt,
  onShowKeymap,
  running,
}: Props) {
  const { theme, toggle } = useTheme();

  useEffect(() => {
    if (!open) return;
    // Close on Escape (cmdk emits its own but the dialog swallows it in some hosts).
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenChange(false);
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
    };
  }, [open, onOpenChange]);

  const pick = (fn: () => void) => {
    onOpenChange(false);
    // Defer so the dialog can unmount cleanly before we mutate state.
    window.setTimeout(fn, 0);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 max-w-md overflow-hidden">
        <DialogHeader className="sr-only">
          <DialogTitle>Command palette</DialogTitle>
        </DialogHeader>
        <Command className="flex flex-col">
          <div className="flex items-center gap-2 border-b border-[var(--color-border)] px-3">
            <Search className="size-3.5 text-[var(--color-muted-foreground)]" />
            <Command.Input
              placeholder="Search actions & prompts…"
              className="flex-1 bg-transparent py-2.5 text-sm outline-none placeholder:text-[var(--color-muted-foreground)]"
            />
          </div>
          <Command.List className="max-h-80 overflow-y-auto p-1">
            <Command.Empty className="p-3 text-center text-xs text-[var(--color-muted-foreground)]">
              No matches.
            </Command.Empty>
            <Command.Group
              heading="Prompts"
              className="text-[11px] text-[var(--color-muted-foreground)] px-2 pt-2"
            >
              {starters.map((s) => {
                const Icon = s.icon;
                return (
                  <Command.Item
                    key={s.label}
                    value={`${s.label} ${s.prompt}`}
                    disabled={running}
                    onSelect={() =>
                      pick(() => {
                        onPickPrompt(s.prompt);
                      })
                    }
                    className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm data-[selected=true]:bg-[var(--color-secondary)] cursor-pointer data-[disabled=true]:opacity-50"
                  >
                    <Icon className="size-3.5 text-[var(--color-muted-foreground)]" />
                    <span>{s.label}</span>
                  </Command.Item>
                );
              })}
            </Command.Group>
            <Command.Group
              heading="Settings"
              className="text-[11px] text-[var(--color-muted-foreground)] px-2 pt-2"
            >
              <Command.Item
                value="toggle theme dark light mode"
                onSelect={() => pick(toggle)}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm data-[selected=true]:bg-[var(--color-secondary)] cursor-pointer"
              >
                {theme === "dark" ? (
                  <SunMedium className="size-3.5 text-[var(--color-muted-foreground)]" />
                ) : (
                  <MoonStar className="size-3.5 text-[var(--color-muted-foreground)]" />
                )}
                <span>Switch to {theme === "dark" ? "light" : "dark"} mode</span>
              </Command.Item>
              <Command.Item
                value="keyboard shortcuts help keymap"
                onSelect={() => pick(onShowKeymap)}
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm data-[selected=true]:bg-[var(--color-secondary)] cursor-pointer"
              >
                <Keyboard className="size-3.5 text-[var(--color-muted-foreground)]" />
                <span>Keyboard shortcuts</span>
              </Command.Item>
              <Command.Item
                value="new chat clear"
                onSelect={() =>
                  pick(() => {
                    window.location.reload();
                  })
                }
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm data-[selected=true]:bg-[var(--color-secondary)] cursor-pointer"
              >
                <Sparkles className="size-3.5 text-[var(--color-muted-foreground)]" />
                <span>New chat</span>
              </Command.Item>
              <Command.Item
                value="sign out logout"
                onSelect={() =>
                  pick(() => {
                    void signOut();
                  })
                }
                className="flex items-center gap-2 rounded-md px-2 py-1.5 text-sm data-[selected=true]:bg-[var(--color-secondary)] cursor-pointer"
              >
                <LogOut className="size-3.5 text-[var(--color-muted-foreground)]" />
                <span>Sign out</span>
              </Command.Item>
            </Command.Group>
          </Command.List>
          <div className="border-t border-[var(--color-border)] px-3 py-1.5 text-[10px] text-[var(--color-muted-foreground)]">
            <kbd className="font-mono">↑↓</kbd> navigate · <kbd className="font-mono">↵</kbd> run ·{" "}
            <kbd className="font-mono">esc</kbd> close
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}
